<template>
  <div>
    <h1>Email templates</h1>
    <button class="btn btn-primary" type="button" @click="createTemplate" v-if="$_can('email-templates.store')">
      ADD
    </button>
    <template v-if="!templates">
      <vue-content-loading :width="800" :height="450">
        <rect v-for="x in 15" :key="x" x="10" :y="15*x*1.4" rx="5" ry="5" width="720" height="6" />
      </vue-content-loading>
    </template>
    <template v-else>
      <table class="table m-table table-hover">
        <thead class="m-table__heading">
          <tr>
            <th class="m-table__id">ID</th>
            <th>Email type</th>
            <th>Company</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="template in templates"
              class="pointer"
              :key="template.id"
              @click="$_can('email-templates.show') && editTemplate(template.company_id, template.email_type_id)"
          >
            <td>{{ template.id }}</td>
            <td>{{ template.email_type.name }}</td>
            <td>{{ template.company.name }}</td>
            <td />
          </tr>
          <tr v-if="templates && !templates.length">
            <td colspan="8" class="text-center">
              No results found
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <modal v-if="showModal"
           @close="closeModal"
           :size="'modal-large'"
    >
      <div slot="header">
        <h4>Email template</h4>
      </div>
      <div slot="body">
        <div class="row mb-2">
          <div class="col">
            <div class="select-wrap">
              <label>Company</label>
              <simple-select v-model="template.company_id"
                             :options="companies"
                             placeholder="Select Company"
              />
            </div>
            <error-field :errors="errors" field="company_id" />
          </div>
          <div class="col">
            <div class="select-wrap">
              <label>Types</label>
              <simple-select v-model="template.email_type_id"
                             v-if="template.company_id"
                             :options="templateTypes"
                             placeholder="Select Template Type"
                             @input="setTemplate"
              />
            </div>
            <error-field :errors="errors" field="email_type_id" />
          </div>
        </div>
        <div class="row mb-2">
          <div class="col">
            <div class="select-wrap">
              <label>Email subject</label>
              <input class="form-control" v-model="template.subject" ref="subject"
                     @click="addingVariableOn = 'subject'"
              />
            </div>
            <error-field :errors="errors" field="subject" />
          </div>
        </div>
        <div class="row mb-2">
          <div class="col">
            <div class="select-wrap h-auto">
              <label>Email text</label><!-- TODO: put this in css -->
              <textarea class="form-control select-wrap-textarea" cols="30" rows="10" contenteditable="true"
                        v-model="template.content" ref="ta" @click="addingVariableOn = 'ta'"
              />
            </div>
            <error-field :errors="errors" field="content" />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <label>Variables</label>
            <div v-if="template.company_id">
              <span class="badge badge-primary mx-1 unselectable pointer"
                    v-for="(variable, key, index) in template.variables"
                    :key="index"
                    @dblclick="insertVariable(variable)"
              >{{ key }}</span>
            </div>
            <div v-else class="text-danger">* Please select company and template</div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <div class="row">
          <div class="col-12 text-right">
            <button type="button" class="btn btn-secondary" @click="deleteTemplate"
                    v-if="$_can('email-templates.destroy')"
            >
              Delete template
            </button>
            <button type="button" class="btn btn-primary" @click="save" v-if="$_can('email-templates.update')">
              Save template
            </button>
          </div>
        </div>
        <div class="clearfix" />
      </div>
    </modal>
  </div>
</template>

<script>
import store from 'store';
import axios from 'axios';
import toastr from 'toastr';
import { mapState } from 'vuex';
import Modal from '../../Modal';
import Errors from '../../../classes/Errors';
import ErrorField from '../../ErrorField';

export default {
  name: 'EmailTemplates',
  components: {
    Modal,
    ErrorField,
  },
  data: () => ({
    companyId: store.get('dispatchUser').company_id,
    templates: null,
    template: {
      email_type_id: null,
      subject: '',
      content: '',
      company_id: null,
      variables: [],
    },
    templateTypes: [],
    showModal: false,
    email_text: '',
    addingVariableOn: 'subject',
    errors: new Errors(),
  }),
  computed: {
    ...mapState({
      companies: state => state.companies.companies.data,
    }),
  },
  mounted() {
    this.getAllTemplates();
    this.getEmailTemplateTypes();
  },
  methods: {
    insertVariable(variable) {
      const textarea = this.addingVariableOn === 'subject' ? this.$refs.subject : this.$refs.ta;
      const cursorPosition = textarea.selectionStart;
      const textBefore = textarea.value.substring(0, cursorPosition);
      const textAfter = textarea.value.substring(cursorPosition, textarea.value.length);
      textarea.value = textBefore + variable + textAfter;
      if (this.addingVariableOn === 'subject') {
        this.template.subject = textarea.value;
      } else {
        this.template.content = textarea.value;
      }
      if (textarea.setSelectionRange) {
        textarea.focus();
        textarea.setSelectionRange(
          cursorPosition + variable.length,
          cursorPosition + variable.length,
        );
      }
    },
    getAllTemplates() {
      axios
        .get(`email-templates/all`)
        .then(res => {
          this.templates = res.data.data;
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    createTemplate() {
      this.showModal = true;
    },
    editTemplate(companyId, emailTypeId) {
      axios
        .get(`companies/${companyId}/email-templates/${emailTypeId}`)
        .then(res => {
          this.template = res.data.data;
          this.email_text = res.data.data.content;
          this.showModal = true;
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    setTemplate() {
      axios
        .get(
          `/companies/${this.template.company_id}/email-templates/${this.template.email_type_id}`,
        )
        .then(res => {
          this.$set(this.template, 'subject', res.data.data.subject);
          this.$set(this.template, 'content', res.data.data.content);
          this.$set(this.template, 'variables', res.data.data.variables);
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    getEmailTemplateTypes() {
      axios
        .get(`values/email-template-types`)

        .then(res => {
          this.templateTypes = res.data.data;
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    closeModal() {
      this.showModal = false;
      this.template = {};
    },
    save() {
      if (this.template.company_id !== undefined && this.template.company_id !== null) {
        axios
          .put(`companies/${this.template.company_id}/email-templates`, { ...this.template })
          .then(() => {
            toastr.success('Successfully saved email template');
            this.template = {};
            this.showModal = false;
            this.getAllTemplates();
          })
          .catch(err => {
            this.errors.record(err.response.data.error);
          });
      } else {
        this.errors.record({ company_id: ['Company must be selected.'] });
      }
    },
    deleteTemplate() {
      axios
        .delete(
          `companies/${this.template.company_id}/email-templates/${this.template.email_type_id}`,
        )
        .then(() => {
          toastr.success('Successfully deleted email template');
          this.template = {};
          this.showModal = false;
          this.getAllTemplates();
        })
        .catch(() => {
          toastr.error('Error saving email template', '', { timeOut: 0 });
        });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/settings/Mailing/EmailTemplates.vue